/**
 * Markdown typesetting: normalizes spacing so block elements render like top-tier publications.
 *
 * What: Inserts missing blank lines before ATX headers, setext headers, lists, blockquotes, and
 * fenced code blocks so parsers (e.g. react-markdown + GFM) treat them as block elements.
 * Why: Authors sometimes omit newlines (e.g. paragraph then "## Next section"); this ensures
 * consistent, publication-quality reading experience without editing every post by hand.
 * When: Update when adding new block-element rules or changing CommonMark/GFM compatibility.
 */

/** ATX heading: 1-6 # then space (CommonMark). */
const ATX_HEADING = /^#{1,6}\s/;

/** Setext underline: line is only = or - (with optional trailing spaces). */
const SETEXT_UNDERLINE = /^(=+|-+)\s*$/;

/** Unordered list: optional indent then - or * or + then space. */
const UNORDERED_LIST = /^(\s*)([-*+])\s+/;

/** Ordered list: optional indent then digits then . then space. */
const ORDERED_LIST = /^(\s*)(\d+)\.\s+/;

/** Blockquote: optional indent then >. */
const BLOCKQUOTE = /^\s*>/;

/** Fenced code block start (or end). */
const FENCED_CODE = /^\s*```/;

function isAtxHeading(line: string): boolean {
  return typeof line === 'string' && ATX_HEADING.test(line);
}

function isSetextUnderline(line: string): boolean {
  return typeof line === 'string' && SETEXT_UNDERLINE.test(line);
}

function isListLine(line: string): boolean {
  return (
    typeof line === 'string' &&
    (UNORDERED_LIST.test(line) || ORDERED_LIST.test(line))
  );
}

function isBlockquoteLine(line: string): boolean {
  return typeof line === 'string' && BLOCKQUOTE.test(line);
}

function isFencedCodeLine(line: string): boolean {
  return typeof line === 'string' && FENCED_CODE.test(line);
}

/**
 * Ensure a blank line precedes any line that starts a block element, so markdown parses correctly.
 * Handles setext headings by ensuring a blank line before the heading line when it's followed by === or ---.
 */
export function typesetMarkdown(markdown: string): string {
  if (typeof markdown !== 'string') {
    return '';
  }

  const lines = markdown.split('\n');
  if (lines.length === 0) {
    return '';
  }

  const result: string[] = [];
  let insideFencedBlock = false;

  function ensureBlankBeforeNext(): void {
    if (result.length > 0 && result[result.length - 1] !== '') {
      result.push('');
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = i + 1 < lines.length ? lines[i + 1] : null;
    const prevEmitted = result.length > 0 ? result[result.length - 1] : null;
    const prevPrevEmitted =
      result.length > 1 ? result[result.length - 2] : null;

    // Setext underline: the *previous* line is the setext heading; ensure blank before that heading.
    if (isSetextUnderline(line) && i > 0) {
      const headingLine = lines[i - 1];
      if (headingLine.trim() !== '') {
        // We already emitted the heading on the last iteration. Ensure there's a blank before it.
        if (prevEmitted !== '' && prevPrevEmitted !== '') {
          result.pop(); // remove the heading we just added
          result.push('');
          result.push(headingLine);
        }
      }
      result.push(line);
      continue;
    }

    // Fenced code: only treat opening ``` as block start; track state for closing ```.
    if (isFencedCodeLine(line)) {
      const fencedStart = !insideFencedBlock;
      if (insideFencedBlock) {
        insideFencedBlock = false;
      } else {
        insideFencedBlock = true;
      }
      const needsBlankBefore =
        fencedStart && prevEmitted !== undefined && prevEmitted !== '';
      if (needsBlankBefore) {
        ensureBlankBeforeNext();
      }
      result.push(line);
      continue;
    }

    // Require a blank line before block elements when they follow non-blank content.
    // For list/blockquote, only require blank when *starting* a block (prev not same type).
    const atxOrSetextHeading =
      isAtxHeading(line) || (nextLine !== null && isSetextUnderline(nextLine));
    const listStart = isListLine(line) && !isListLine(prevEmitted ?? '');
    const blockquoteStart =
      isBlockquoteLine(line) && !isBlockquoteLine(prevEmitted ?? '');

    const needsBlankBefore =
      (atxOrSetextHeading || listStart || blockquoteStart) &&
      prevEmitted !== undefined &&
      prevEmitted !== '';

    if (needsBlankBefore) {
      ensureBlankBeforeNext();
    }

    result.push(line);
  }

  return result.join('\n');
}
